import { FC, useMemo } from 'react';
import { Heading } from 'roo-ui/components';
import { BreakOnNewLine, ContentHeading } from 'components/Typography';
import CollapsableCard from 'components/CollapsableCard';
import MessageBoard from 'components/MessageBoard';
import { MaxWidthContent } from './primitives';
import { sanitizeHTML } from 'lib/utils';
import { DetailsBooking } from 'types';

interface KnowBeforeYouGoProps {
  booking: DetailsBooking;
  onToggle: () => any;
}

const CHARACTER_LIMIT = 400;

const KnowBeforeYouGo: FC<KnowBeforeYouGoProps> = ({ booking, onToggle }) => {
  const { checkInInstructions, hotelPolicies } = useMemo(
    () => ({
      checkInInstructions: sanitizeHTML(booking.reservation.property.checkInInstructions),
      hotelPolicies: sanitizeHTML(booking.reservation.property.roomInformation),
    }),
    [booking],
  );

  if (booking.reservation.isAfterCheckOut || !booking.flags.isBooked || (!checkInInstructions && !hotelPolicies)) {
    return null;
  }

  const isAboveCharacterLimit = checkInInstructions.length + hotelPolicies.length > CHARACTER_LIMIT;

  return (
    <>
      <ContentHeading>Know before you go</ContentHeading>

      <MessageBoard mb="5" mt="-8px">
        <strong>Please note,</strong> there is important information to know before checking in.
      </MessageBoard>

      <CollapsableCard onToggle={onToggle} collapsable={isAboveCharacterLimit}>
        <MaxWidthContent>
          {checkInInstructions && (
            <>
              <Heading.h4 mt={6} data-testid="check-in-instructions-heading">
                Check-In Instructions
              </Heading.h4>
              <BreakOnNewLine dangerouslySetInnerHTML={{ __html: checkInInstructions }} />
            </>
          )}

          {hotelPolicies && (
            <>
              <Heading.h4 mt={6} data-testid="hotel-policies-heading">
                Hotel Policies
              </Heading.h4>
              <BreakOnNewLine dangerouslySetInnerHTML={{ __html: hotelPolicies }} />
            </>
          )}
        </MaxWidthContent>
      </CollapsableCard>
    </>
  );
};

export default KnowBeforeYouGo;
